import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useDocumentTitle } from '../hooks/use-document-title';
import { ContactDetails } from '../components/ContactDetails';

export default function TermsAndConditions() {
  useDocumentTitle('Terms and Conditions');
  const lastUpdated = "February 2, 2026";

  return (
    <div className="min-h-screen flex flex-col bg-paper">
      <Navbar />
      <main id="main-content" className="flex-1 w-full max-w-4xl mx-auto px-4 sm:px-6 pt-28 pb-16">
        <article className="bg-white rounded-lg shadow-card border border-border p-6 sm:p-8 md:p-12">
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-ink mb-4">Terms and Conditions</h1>
          <p className="text-gray-600 mb-8">Last Updated: {lastUpdated}</p>

          <div className="max-w-none space-y-8">
            <section>
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-ink mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                By accessing and using this service, you accept and agree to be bound by the terms and 
                provision of this agreement. If you do not agree to abide by the above, please do not 
                use this service.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-ink mb-4">2. Service Description</h2>
              <p className="text-gray-700 leading-relaxed">
                Our service provides a platform for creating, customizing, and ordering custom invitations. 
                By using our service, you agree to receive transactional messages regarding your custom 
                invitation orders. Messages may include order confirmations, design proofs, status updates, 
                and other communications related to your account and orders.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-ink mb-4">3. User Accounts</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                To use certain features of our service, you must create an account. You agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Provide accurate, current, and complete information when creating your account</li>
                <li>Maintain and update your account information to keep it accurate</li>
                <li>Maintain the security of your account credentials</li>
                <li>Accept responsibility for all activities that occur under your account</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
              </ul>
            </section>

            <section>
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-ink mb-4">4. Orders and Payment</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                When you place an order through our service:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>You agree to provide accurate order information</li>
                <li>All prices are subject to change without notice</li>
                <li>Payment must be received before order processing begins</li>
                <li>We reserve the right to refuse or cancel any order at our discretion</li>
                <li>Refunds are subject to our refund policy, which may vary by product type</li>
              </ul>
            </section>

            <section>
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-ink mb-4">5. SMS Service and Messaging</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>Program Description:</strong> By providing your phone number and opting in to our 
                SMS service, you agree to receive transactional messages from us regarding your custom 
                invitation orders. Messages may include order confirmations, design proofs, and status updates.
              </p>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>Cancellation:</strong> You can cancel the SMS service at any time. Just text "STOP" 
                to the service number. After you send the SMS message "STOP" to us, we will send you an SMS 
                message to confirm that you have been unsubscribed. After this, you will no longer receive 
                SMS messages from us. If you want to join again, just sign up as you did the first time and 
                we will start sending SMS messages to you again.
              </p>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>Help:</strong> If you are experiencing issues with the messaging program, you can 
                reply with the keyword "HELP" for more assistance, or you can get help directly through 
                our customer support system.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>Fees:</strong> Message and data rates may apply for any messages sent to you from 
                us and to us from you. Message frequency varies. If you have any questions about your text 
                plan or data plan, it is best to contact your wireless provider.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-ink mb-4">6. Carriers</h2>
              <p className="text-gray-700 leading-relaxed">
                Carriers are not liable for delayed or undelivered messages. We are not responsible for 
                messages that are not received due to carrier issues, network problems, or other technical 
                difficulties beyond our control.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-ink mb-4">7. Intellectual Property</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                All content, designs, templates, and materials available through our service are protected 
                by copyright, trademark, and other intellectual property laws. You may not:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Copy, reproduce, or distribute any content without permission</li>
                <li>Use our designs or templates for commercial purposes beyond your personal invitations</li>
                <li>Remove or alter any copyright or proprietary notices</li>
                <li>Create derivative works based on our templates without authorization</li>
              </ul>
            </section>

            <section>
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-ink mb-4">8. User Content</h2>
              <p className="text-gray-700 leading-relaxed">
                You retain ownership of any content you create or upload through our service. By using our 
                service, you grant us a license to use, display, and process your content solely for the 
                purpose of providing our services to you. You are responsible for ensuring that your content 
                does not infringe on the rights of others or violate any laws.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-ink mb-4">9. Prohibited Uses</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                You agree not to use our service:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>For any unlawful purpose or to solicit others to perform unlawful acts</li>
                <li>To violate any international, federal, provincial, or state regulations, rules, laws, or local ordinances</li>
                <li>To infringe upon or violate our intellectual property rights or the intellectual property rights of others</li>
                <li>To harass, abuse, insult, harm, defame, slander, disparage, intimidate, or discriminate</li>
                <li>To submit false or misleading information</li>
                <li>To upload or transmit viruses or any other type of malicious code</li>
              </ul>
            </section>

            <section>
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-ink mb-4">10. Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed">
                In no event shall we, our directors, employees, partners, agents, suppliers, or affiliates 
                be liable for any indirect, incidental, special, consequential, or punitive damages, including 
                without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting 
                from your use of the service.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-ink mb-4">11. Disclaimer</h2>
              <p className="text-gray-700 leading-relaxed">
                The information on this service is provided on an "as is" basis. To the fullest extent permitted 
                by law, we exclude all representations, warranties, conditions, and terms relating to our 
                service and the use of this service.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-ink mb-4">12. Changes to Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right, at our sole discretion, to modify or replace these Terms and Conditions 
                at any time. If a revision is material, we will provide at least 30 days notice prior to any 
                new terms taking effect. What constitutes a material change will be determined at our sole 
                discretion.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-ink mb-4">13. Contact Information</h2>
              <ContactDetails topic="these Terms and Conditions" />
            </section>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}
